import React, { useState, useEffect, useMemo } from "react";
import { Star, X, RefreshCcw, Heart, Ruler, ShoppingBag, ShieldCheck, Search, ChevronDown, Layers, ArrowRight, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import axios from "axios";
import SEO from '../components/SEO';


// --- PREMIUM HUD ALERT ---
const CustomAlert = ({ message, onClose }) => (
  <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[3000] w-[90%] max-w-md animate-in fade-in slide-in-from-top-4 duration-500">
    <div className="bg-white text-black px-6 py-4 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex items-center justify-between border border-emerald-500/30 backdrop-blur-xl">
      <div className="flex items-center gap-3">
        <div className="bg-emerald-500 p-1 rounded-full text-white"><CheckCircle2 size={16} /></div>
        <span className="font-black uppercase text-[10px] tracking-tighter italic">{message}</span>
      </div>
      <button onClick={onClose} className="p-1 hover:rotate-90 transition-transform bg-zinc-100 rounded-full"><X size={14}/></button>
    </div>
  </div>
);

const SmartMedia = ({ product }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = useMemo(() => {
    const gallery = product.gallery || [];
    return gallery.length > 0 ? gallery : [product.img];
  }, [product]);

  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images]);

  return (
    <div className="relative w-full h-full overflow-hidden group/media">
      <div className="flex h-full transition-transform duration-1000 ease-[cubic-bezier(0.23,1,0.32,1)]" 
           style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {images.map((img, idx) => (
          <img 
            key={idx} 
            src={img} 
            // 🔥 SEO Magic: Alt text mein Brand aur Name dono
            alt={`${product.brand} ${product.name} - View ${idx + 1}`} 
            className="w-full h-full object-cover shrink-0" 
            crossOrigin="anonymous" 
            // ⚡ Performance
            loading="lazy" 
          />
        ))}
      </div>
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1 z-10">
          {images.map((_, i) => (
            <div key={i} className={`h-1 rounded-full transition-all duration-500 ${currentIndex === i ? "w-6 bg-emerald-500" : "w-1 bg-white/40"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const AllProducts = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlistItems } = useCart();

  const [dbProducts, setDbProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [searchQuery, setSearchQuery] = useState("");
  const [alertMsg, setAlertMsg] = useState("");
  const [quickView, setQuickView] = useState(null);
  const [selectedSizeData, setSelectedSizeData] = useState(null);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get("https://wethrift-backend.onrender.com/api/product");
        setDbProducts(res.data);
      } catch (err) { console.error(err); } 
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const filteredProducts = useMemo(() => {
    let list = dbProducts.filter(p => {
      const catMatch = selectedCategory === "all" || p.category === selectedCategory;
      const brandMatch = selectedBrand === "all" || p.brand === selectedBrand;
      const searchMatch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
      return catMatch && brandMatch && searchMatch;
    });
    if (sortBy === "low") list.sort((a, b) => a.price - b.price);
    if (sortBy === "high") list.sort((a, b) => b.price - a.price);
    return list;
  }, [dbProducts, selectedCategory, selectedBrand, searchQuery, sortBy]);

  const categories = useMemo(() => ["all", ...new Set(dbProducts.map(p => p.category))].filter(Boolean), [dbProducts]);
  const brands = useMemo(() => {
    const base = selectedCategory === "all" ? dbProducts : dbProducts.filter(p => p.category === selectedCategory);
    return ["all", ...new Set(base.map(p => p.brand))].filter(Boolean);
  }, [dbProducts, selectedCategory]);

  const isWishlisted = (id) => wishlistItems.some(item => item._id === id);

  return (


    <>
    <SEO 
  title="Explore Archive | Full Collection" 
  description="Browse our complete catalog of authenticated thrifted gems. From vintage hoodies to rare denims, find your next asset here."
  url="/products"
/>

   <div className={`min-h-screen transition-colors duration-300 ${
  isDarkMode 
  ? "bg-[#050505] text-white" 
  : "bg-white text-black"
}`}>
      {alertMsg && <CustomAlert message={alertMsg} onClose={() => setAlertMsg("")} />}

      <header className="pt-24 pb-8 px-6 border-b border-white/5 bg-zinc-950/20 backdrop-blur-3xl">
        <div className="max-w-[1400px] mx-auto text-center">
          <h1 className="text-5xl font-black italic uppercase tracking-tighter">
    Archive <span className="text-emerald-500">Protocol</span>
  </h1>
          <div className="w-full max-w-4xl mx-auto space-y-6">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1 group">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-emerald-500" size={16} />
                <input 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="SEARCH ASSETS..."
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-8 outline-none focus:border-emerald-500 transition-all uppercase text-[10px] font-bold tracking-widest"
                />
              </div>
              <div className="relative">
                <select onChange={(e) => setSortBy(e.target.value)} className="w-full appearance-none bg-white text-black font-black py-4 px-10 rounded-2xl outline-none text-[10px] uppercase cursor-pointer">
                  <option value="default">SORT_BY</option>
                  <option value="low">PRICE_MIN</option>
                  <option value="high">PRICE_MAX</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-black pointer-events-none" size={14} />
              </div>
            </div>
            <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-2">
              {categories.map(cat => (
                <button key={cat} onClick={() => { setSelectedCategory(cat); setSelectedBrand("all"); }} className={`whitespace-nowrap px-6 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest border transition-all ${selectedCategory === cat ? "bg-emerald-500 border-emerald-500 text-black" : "bg-transparent border-white/10 text-zinc-500"}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row gap-8 p-4 lg:p-12">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-28 space-y-8">
            <div className="flex items-center gap-3 px-2">
              <Layers className="text-emerald-500" size={14} />
              <h2 className="text-[10px] font-black uppercase tracking-widest italic">Asset_Registry</h2>
            </div>
            <div className="flex flex-col gap-1">
              {brands.map(brand => (
                <button key={brand} onClick={() => setSelectedBrand(brand)} className={`text-left px-5 py-3.5 rounded-xl text-[10px] font-black uppercase transition-all ${selectedBrand === brand ? "bg-white/10 text-emerald-500 shadow-xl" : "text-zinc-500 hover:text-white"}`}>
                  {brand}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="flex-1">
          {loading ? (
            <div className="h-96 flex flex-col items-center justify-center opacity-40 animate-pulse">
              <RefreshCcw className="animate-spin text-emerald-500 mb-4" size={32} />
              <p className="text-[9px] font-black uppercase tracking-[0.3em]">Syncing_Database...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-10">
              {filteredProducts.map(product => (
                <div key={product._id} className="group relative flex flex-col bg-zinc-950/20 rounded-[2.5rem] border border-white/5 overflow-hidden transition-all hover:border-emerald-500/30">
                  <div className="cursor-pointer flex flex-col h-full" onClick={() => navigate(`/product/${product._id}`)}>
                    <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden m-2 shadow-2xl">
                      <SmartMedia product={product} />
                      <div className="absolute top-4 right-4 z-20">
                        <button 
                          onClick={(e) => { e.stopPropagation(); toggleWishlist(product); setAlertMsg(isWishlisted(product._id) ? "REMOVED" : "ARCHIVED"); }}
                          className={`p-3 rounded-full backdrop-blur-3xl transition-all border border-white/10 ${isWishlisted(product._id) ? "bg-red-500 text-white" : "bg-black/50 text-white"}`}
                        >
                          <Heart size={16} fill={isWishlisted(product._id) ? "white" : "none"} />
                        </button>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setQuickView(product); setSelectedSizeData(null); }}
                        className="absolute inset-x-6 bottom-6 py-4 bg-white text-black rounded-xl font-black uppercase text-[10px] tracking-widest opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 flex items-center justify-center gap-2"
                      >
                        <ShoppingBag size={14} /> Quick_View
                      </button>
                    </div>
                    <div className="p-6 flex justify-between items-start">
                      <div className="space-y-1">
                        <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest italic">{product.brand}</p>
                        <h3 className="text-lg font-black uppercase italic leading-none truncate w-40 md:w-full">{product.name}</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-black italic tracking-tighter">₹{product.price}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* --- QUICK VIEW POPUP (LOCKED VIEWPORT) --- */}
      {quickView && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/90 backdrop-blur-xl p-4 md:p-10">
          <div className="relative w-full max-w-5xl bg-[#0a0a0a] border border-white/10 rounded-[3rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.8)] flex flex-col md:flex-row h-full max-h-[650px] animate-in zoom-in-95 duration-500">
            
            <button onClick={() => setQuickView(null)} className="absolute top-6 right-6 z-[1600] p-3 bg-white text-black rounded-full hover:rotate-90 transition-transform shadow-2xl">
              <X size={20}/>
            </button>

            {/* Left: Fixed Image */}
            <div className="w-full md:w-1/2 h-64 md:h-full bg-zinc-900 border-b md:border-b-0 md:border-r border-white/5">
                <SmartMedia product={quickView} />
            </div>

            {/* Right: Scrollable Info */}
            <div className="w-full md:w-1/2 flex flex-col h-full bg-[#0a0a0a]">
              <div className="flex-1 overflow-y-auto p-8 md:p-12 custom-scrollbar">
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-emerald-500 text-black text-[9px] font-black px-4 py-1 rounded-full uppercase italic tracking-widest">{quickView.brand}</span>
                    <span className="text-zinc-600 text-[9px] font-black uppercase italic">ID_{quickView._id.slice(-6)}</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-black italic uppercase mb-4 tracking-tighter leading-[0.9]">{quickView.name}</h2>
                  <p className="text-zinc-500 text-[10px] leading-relaxed uppercase font-bold border-l-2 border-emerald-500 pl-4">{quickView.description || "PREMIUM SELECTION."}</p>
                </div>

                <div className="mb-10">
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-[9px] font-black uppercase text-zinc-600 tracking-widest italic">Configuration</p>
                    <button onClick={() => setShowSizeGuide(true)} className="text-[10px] font-black text-emerald-500 flex items-center gap-2 underline uppercase"><Ruler size={12}/> GUIDE</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickView.sizeStock?.map((item) => (
                      <button key={item.size} disabled={item.quantity <= 0} onClick={() => setSelectedSizeData(item)} 
                        className={`px-6 py-4 rounded-xl text-[10px] font-black uppercase transition-all border ${item.quantity <= 0 ? "opacity-20 cursor-not-allowed" : selectedSizeData?.size === item.size ? "bg-emerald-500 border-emerald-500 text-black scale-105" : "bg-white/5 border-white/10 hover:border-emerald-500"}`}>
                        {item.size}
                      </button>
                    ))}
                  </div>
                  {selectedSizeData && <p className="mt-4 text-[9px] font-black text-emerald-500 uppercase flex items-center gap-2 italic animate-pulse"><ShieldCheck size={12}/> {selectedSizeData.quantity} UNITS_IN_STOCK</p>}
                </div>
              </div>

              {/* Bottom Fixed Action */}
              <div className="p-8 border-t border-white/5 bg-zinc-950/50">
                <div className="flex items-center justify-between mb-6">
                   <p className="text-3xl font-black text-white italic tracking-tighter">₹{quickView.price}</p>
                   <ShoppingBag size={24} className="text-emerald-500 opacity-30" />
                </div>
                <button 
                  onClick={() => { if (!selectedSizeData) return setAlertMsg("SELECT_SIZE"); addToCart(quickView, selectedSizeData.size); setAlertMsg("ADDED"); setQuickView(null); }}
                  className="w-full bg-emerald-500 text-black py-6 rounded-2xl font-black uppercase text-[11px] tracking-[0.3em] hover:bg-white transition-all shadow-[0_10px_40px_rgba(16,185,129,0.2)]"
                >
                  INITIALIZE_ACQUISITION
                </button>
              </div>
            </div>

            {/* NESTED SIZE GUIDE (Fits inside parent) */}
            {showSizeGuide && (
                <div className="absolute inset-0 z-[1700] bg-black/95 p-6 flex items-center justify-center animate-in fade-in duration-300">
                    <div className="w-full max-w-sm bg-zinc-900 border border-white/10 rounded-[2.5rem] p-8 relative">
                        <button onClick={() => setShowSizeGuide(false)} className="absolute top-6 right-6 text-white hover:rotate-90 transition-transform"><X size={24}/></button>
                        <h3 className="text-xl font-black italic uppercase mb-6 tracking-tighter text-emerald-500 underline underline-offset-8">METRIC_REGISTRY</h3>
                        <div className="overflow-hidden border border-white/5 rounded-2xl">
                          <table className="w-full text-left text-[10px]">
                              <thead className="bg-zinc-800 text-zinc-500 font-black uppercase">
                                <tr><th className="p-4">SIZE</th><th className="p-4">CHEST</th><th className="p-4">LEN</th></tr>
                              </thead>
                              <tbody className="bg-black text-zinc-300 font-bold">
                                  {quickView.sizeChart?.map((row, i) => (
                                      <tr key={i} className="border-t border-white/5"><td className="p-4 text-emerald-500 uppercase font-black">{row.label}</td><td className="p-4">{row.val1}</td><td className="p-4">{row.val2}</td></tr>
                                  ))}
                              </tbody>
                          </table>
                        </div>
                    </div>
                </div>
            )}
          </div>
        </div>
      )}
    </div>
    
    </>
  );
};

export default AllProducts;